import { FC, SetStateAction, useCallback, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useSnackbar } from '@app/context/SnackbarContext'

import { Button } from '@ui/components/Button'
import { Input, PopoverInput } from '@ui/components/Input'

import { ReferralMaterialPreviewDto, ReferralMaterialPreviewUrls } from '@model/ReferralMaterialDto'

import { Box } from '@shared/components/Box'

import { CUSTOM_URL_PROMO_OPTION_ID, PromoAssetFields } from './PromoPreview.modal'
import { PromoUrlOutputInput } from './PromoUrlOutputInput'

interface Props {
  referralMaterial: ReferralMaterialPreviewDto
  fields: PromoAssetFields
  setField: (value: SetStateAction<PromoAssetFields>) => void
  isRefMaterialLoading: boolean
  isLinkLoading: boolean
  isLinkShortened: boolean
  isDefaultTargetUrlSelected: boolean | null
  onIBLinkGeneration: (shortenUrl: boolean) => void
}

const customUrlRegEx =
  /^((https?):\/\/)?([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(\/[a-zA-Z0-9\-._~!?$%&'()*+,;=:@/]*)?$/

export const PromoPreviewUrlFields: FC<Props> = ({
  referralMaterial,
  fields,
  setField,
  isRefMaterialLoading,
  isLinkLoading,
  isLinkShortened,
  isDefaultTargetUrlSelected,
  onIBLinkGeneration,
}) => {
  const { t } = useTranslation()
  const { addSnackbar } = useSnackbar()
  const customTargetUrlRef = useRef<HTMLInputElement>(null)

  const [selectedReferralMaterialUrls, setSelectedReferralMaterialUrls] =
    useState<Omit<ReferralMaterialPreviewUrls, 'id'>>()

  const isCustomTargetUrlValid = useCallback(
    (value: string) => {
      const currentTargetUrl = value
      const previousTargetUrl = fields.customTargetUrl

      if (!currentTargetUrl) {
        addSnackbar.info({ message: t('Validation.Please, enter the link to generate') })
        customTargetUrlRef.current && (customTargetUrlRef.current.value = '')

        return false
      } else if (previousTargetUrl === currentTargetUrl) {
        addSnackbar.info({ message: t('Validation.Please, enter different target URL') })

        return false
      }

      if (currentTargetUrl && !customUrlRegEx.test(currentTargetUrl)) {
        addSnackbar.error({ message: t('Validation.Please, enter a valid URL') })
        customTargetUrlRef.current && (customTargetUrlRef.current.value = value)

        return false
      }

      return true
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [fields.customTargetUrl, t]
  )

  const getOptions = () => [
    { id: CUSTOM_URL_PROMO_OPTION_ID, name: t('Custom') },
    ...(referralMaterial.urls
      ? referralMaterial.urls.map((url) => ({
          id: url.alias,
          name: url.alias,
        }))
      : []),
  ]

  const handleChange = (url: { id: string; name: string }) => {
    const referralMaterialUrls = referralMaterial.urls.find(
      (referralMaterialUrl) => referralMaterialUrl.alias === url.name
    )

    if (url.id === CUSTOM_URL_PROMO_OPTION_ID) {
      setSelectedReferralMaterialUrls({
        alias: 'Custom',
        urls: [{ id: CUSTOM_URL_PROMO_OPTION_ID, name: t('Custom') }],
      })
    } else if (referralMaterialUrls) {
      setSelectedReferralMaterialUrls(referralMaterialUrls)
    }

    if (referralMaterialUrls?.urls.length === 1) {
      if (fields.defaultTargetUrl?.id === url.id) {
        return
      }

      setField((prevFields) => ({
        ...prevFields,
        defaultTargetUrl: referralMaterialUrls?.urls[0],
      }))
    } else if (url.id === CUSTOM_URL_PROMO_OPTION_ID) {
      if (fields.defaultTargetUrl?.id === url.id) {
        return
      }

      if (url.id === CUSTOM_URL_PROMO_OPTION_ID && customTargetUrlRef.current) {
        customTargetUrlRef.current.value = ''
      }

      setField((prevFields) => ({
        ...prevFields,
        defaultTargetUrl: url,
        urlOutput: null,
        customTargetUrl: null,
      }))
    }
  }

  return (
    <>
      <Box flex gap='x8' fullWidth>
        <Box fullWidth>
          <PopoverInput
            size='lg'
            value={
              selectedReferralMaterialUrls?.alias === 'Custom'
                ? '0'
                : selectedReferralMaterialUrls?.alias
            }
            disabled={isRefMaterialLoading}
            dropdownListVariant='compact'
            label={t('Referral Materials.Default target URLs')}
            options={getOptions()}
            onChange={(url) => handleChange(url)}
          />
        </Box>
        <Button.Primary
          size='md'
          disabled={
            !fields.defaultTargetUrl ||
            (fields.defaultTargetUrl.id === CUSTOM_URL_PROMO_OPTION_ID &&
              !fields.customTargetUrl) ||
            isRefMaterialLoading
          }
          onClick={() => {
            const url = isDefaultTargetUrlSelected
              ? fields.defaultTargetUrl?.name
              : fields.customTargetUrl

            url && window.open(url, '_blank')
          }}
        >
          {t('Preview')}
        </Button.Primary>
      </Box>

      {selectedReferralMaterialUrls && selectedReferralMaterialUrls.urls.length > 1 ? (
        <Box fullWidth>
          <PopoverInput
            size='lg'
            value={fields.defaultTargetUrl?.id}
            disabled={isRefMaterialLoading}
            dropdownListVariant='compact'
            label={t('Referral Materials.Select target URL')}
            options={[...(selectedReferralMaterialUrls.urls ?? [])]}
            onChange={(url) => {
              if (fields.defaultTargetUrl?.id === url.id) {
                return
              }

              setField((prevFields) => ({
                ...prevFields,
                defaultTargetUrl: url,
              }))
            }}
          />
        </Box>
      ) : null}

      {fields.defaultTargetUrl?.id === CUSTOM_URL_PROMO_OPTION_ID && (
        <Box flex gap='x8' fullWidth>
          <Input
            label={t('Referral Materials.Custom target URL')}
            disabled={isRefMaterialLoading}
            ref={customTargetUrlRef}
            fullWidth
            size='lg'
          />
          <Button.Primary
            size='md'
            onClick={() => {
              const value = customTargetUrlRef.current?.value.trim() ?? ''
              const isValid = isCustomTargetUrlValid(value)

              if (!isValid) {
                return
              }

              setField((prevFields) => ({
                ...prevFields,
                customTargetUrl: value || null,
              }))
            }}
          >
            {t('Referral Materials.Generate')}
          </Button.Primary>
        </Box>
      )}

      {fields.urlOutput && (
        <Box flex gap='x8' fullWidth>
          <Box fullWidth>
            <PromoUrlOutputInput
              value={fields.urlOutput || ''}
              isLinkLoading={isLinkLoading}
              label={t('Referral Materials.URL output')}
            />
          </Box>
          <Button.Primary
            size='md'
            loading={isLinkLoading || isRefMaterialLoading}
            disabled={isLinkShortened}
            onClick={() => onIBLinkGeneration(true)}
          >
            {t('Shorten')}
          </Button.Primary>
        </Box>
      )}
    </>
  )
}
